/**
 * ============================================================================
 * featuredTablesService.ts - FEATURED TABLES SERVICE
 * ============================================================================
 * 
 * This service manages the 4 always-on featured tables that provide instant
 * play opportunities for users without requiring table creation.
 * 
 * Featured Tables:
 * 1. PlayCoin Low:  10/20 blinds, 400-2,000 buy-in
 * 2. PlayCoin Mid:  50/100 blinds, 2,000-10,000 buy-in
 * 3. PKCash Micro:  $0.01/$0.02 blinds, $0.25-$2 buy-in
 * 4. PKCash Low:    $0.05/$0.10 blinds, $1-$10 buy-in
 * 
 * ============================================================================
 */

import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// ============================================================================
// TYPES
// ============================================================================

export interface FeaturedTable {
  id: string;
  name: string;
  currency: 'playcoins' | 'pkcash';
  tier: 'micro' | 'low' | 'mid';
  smallBlind: number;
  bigBlind: number;
  minBuyIn: number;
  maxBuyIn: number;
  maxSeats: number;
  players: string[];
  playerCount: number;
  status: 'waiting' | 'playing' | 'ready';
  isFeatured: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // Display helpers
  blindsDisplay: string;
  buyInDisplay: string;
  currencyIcon: string;
  tierLabel: string;
  playerNames?: { odid: string; username: string; photoURL?: string }[];
  // Theme
  tableTheme?: 'classic' | 'premium';
  dealerImageUrl?: string;
}

export interface FeaturedTablesConfig {
  playcoins: {
    low: FeaturedTableDefinition;
    mid: FeaturedTableDefinition;
  };
  pkcash: {
    micro: FeaturedTableDefinition;
    low: FeaturedTableDefinition;
  };
}

export interface FeaturedTableDefinition {
  name: string;
  smallBlind: number;
  bigBlind: number;
  minBuyIn: number;
  maxBuyIn: number;
  maxSeats: number;
  tierLabel: string;
}

// ============================================================================
// FEATURED TABLES CONFIGURATION
// ============================================================================

export const FEATURED_TABLES_CONFIG: FeaturedTablesConfig = {
  playcoins: {
    low: {
      name: 'Quick Game - Low Stakes',
      smallBlind: 10,
      bigBlind: 20,
      minBuyIn: 400,      // 20 BB
      maxBuyIn: 2000,     // 100 BB
      maxSeats: 8,
      tierLabel: 'Low Stakes',
    },
    mid: {
      name: 'Quick Game - Mid Stakes',
      smallBlind: 50,
      bigBlind: 100,
      minBuyIn: 2000,     // 20 BB
      maxBuyIn: 10000,    // 100 BB
      maxSeats: 8,
      tierLabel: 'Mid Stakes',
    },
  },
  pkcash: {
    micro: {
      name: 'Micro Stakes',
      smallBlind: 0.01,
      bigBlind: 0.02,
      minBuyIn: 0.25,     // ~12.5 BB
      maxBuyIn: 2.00,     // 100 BB
      maxSeats: 8,
      tierLabel: 'Micro',
    },
    low: {
      name: 'Low Stakes',
      smallBlind: 0.05,
      bigBlind: 0.10,
      minBuyIn: 1.00,     // 10 BB
      maxBuyIn: 10.00,    // 100 BB
      maxSeats: 8,
      tierLabel: 'Low Stakes',
    },
  },
};

// ============================================================================
// FEATURED TABLE IDS
// ============================================================================

/**
 * Generate consistent featured table ID
 * Format: featured-{currency}-{tier}
 */
export function getFeaturedTableId(currency: 'playcoins' | 'pkcash', tier: string): string {
  return `featured-${currency}-${tier}`;
}

/**
 * Check if a table ID is a featured table
 */
export function isFeaturedTableId(tableId: string): boolean {
  return tableId.startsWith('featured-');
}

/**
 * Get all expected featured table IDs
 */
export function getAllFeaturedTableIds(): string[] {
  return [
    getFeaturedTableId('playcoins', 'low'),
    'featured-playcoins-sophia',
    getFeaturedTableId('pkcash', 'micro'),
    getFeaturedTableId('pkcash', 'low'),
  ];
}

// ============================================================================
// FETCH FEATURED TABLES
// ============================================================================

/**
 * Fetch all featured tables from Firestore
 * Returns array of FeaturedTable objects with display helpers
 */
export async function getFeaturedTables(): Promise<FeaturedTable[]> {
  try {
    const tablesRef = collection(db, 'tables');
    // No orderBy here: it forced a composite index that production never
    // had, so THIS query threw while the identical subscription (no
    // orderBy) worked - Quick Match said "no tables" with four bot tables
    // sitting live. sortFeaturedTables() below orders client-side anyway.
    const q = query(
      tablesRef,
      where('isFeatured', '==', true),
      where('status', 'in', ['waiting', 'ready', 'playing'])
    );

    const snapshot = await getDocs(q);
    const tables: FeaturedTable[] = [];

    for (const doc of snapshot.docs) {
      const data = doc.data();
      const table = transformToFeaturedTable(doc.id, data);
      if (table) {
        tables.push(table);
      }
    }

    // Sort by currency (playcoins first) then by tier
    return sortFeaturedTables(tables);
  } catch (error) {
    console.error('❌ [FeaturedTables] Error fetching:', error);
    return [];
  }
}

/**
 * Subscribe to featured tables with real-time updates
 */
export function subscribeToFeaturedTables(
  callback: (tables: FeaturedTable[]) => void
): () => void {
  const tablesRef = collection(db, 'tables');
  const q = query(
    tablesRef,
    where('isFeatured', '==', true),
    where('status', 'in', ['waiting', 'ready', 'playing'])
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const tables: FeaturedTable[] = [];

      for (const doc of snapshot.docs) {
        const data = doc.data();
        const table = transformToFeaturedTable(doc.id, data);
        if (table) {
          tables.push(table);
        }
      }

      callback(sortFeaturedTables(tables));
    },
    (error) => {
      console.error('❌ [FeaturedTables] Subscription error:', error);
      callback([]);
    }
  );
}

/**
 * Get featured tables by currency
 */
export async function getFeaturedTablesByCurrency(
  currency: 'playcoins' | 'pkcash'
): Promise<FeaturedTable[]> {
  const allTables = await getFeaturedTables();
  return allTables.filter(t => t.currency === currency);
}

// ============================================================================
// ENSURE FEATURED TABLES EXIST
// ============================================================================

/**
 * Call cloud function to ensure all featured tables exist
 * This is called on page load as a fallback to the scheduled function
 */
export async function ensureFeaturedTablesExist(): Promise<{
  success: boolean;
  created: string[];
  existing: string[];
  error?: string;
}> {
  try {
    const ensureFeaturedTables = httpsCallable<
      {},
      { success: boolean; created: string[]; existing: string[]; error?: string }
    >(functions, 'ensureFeaturedTables');

    const result = await ensureFeaturedTables({});
    
    if (result.data.created.length > 0) {
      console.log('✅ [FeaturedTables] Created missing tables:', result.data.created);
    }

    return result.data;
  } catch (error) {
    console.error('❌ [FeaturedTables] Error ensuring tables exist:', error);
    return {
      success: false,
      created: [],
      existing: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Transform Firestore document to FeaturedTable
 */
function transformToFeaturedTable(id: string, data: any): FeaturedTable | null {
  if (!data) return null;

  const currency = data.currency || 'playcoins';
  const smallBlind = data.smallBlind || 0;
  const bigBlind = data.bigBlind || 0;
  const minBuyIn = data.minBuyIn || 0;
  const maxBuyIn = data.maxBuyIn || 0;
  const players = data.players || [];

  // Determine tier based on blinds
  let tier: 'micro' | 'low' | 'mid' = 'low';
  if (currency === 'pkcash') {
    tier = bigBlind <= 0.02 ? 'micro' : 'low';
  } else {
    tier = bigBlind <= 20 ? 'low' : 'mid';
  }

  // Format display strings
  const blindsDisplay = currency === 'pkcash'
    ? `$${smallBlind.toFixed(2)}/$${bigBlind.toFixed(2)}`
    : `${smallBlind.toLocaleString()}/${bigBlind.toLocaleString()}`;

  const buyInDisplay = currency === 'pkcash'
    ? `$${minBuyIn.toFixed(2)} - $${maxBuyIn.toFixed(2)}`
    : `${minBuyIn.toLocaleString()} - ${maxBuyIn.toLocaleString()}`;

  const currencyIcon = currency === 'pkcash' ? '' : '';

  
  // Get tier label from config
  const tierLabel = currency === 'pkcash'
    ? (tier === 'micro' ? 'Micro' : 'Low Stakes')
    : (tier === 'low' ? 'Low Stakes' : 'Mid Stakes');

  return {
    id,
    name: data.name || `Featured ${tierLabel}`,
    currency,
    tier,
    smallBlind,
    bigBlind,
    minBuyIn,
    maxBuyIn,
    maxSeats: data.maxSeats || 8,
    players,
    playerCount: players.length,
    status: data.status || 'waiting',
    isFeatured: true,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    blindsDisplay,
    buyInDisplay,
    currencyIcon,
    tierLabel,
    playerNames: data.playerNames || [],
    tableTheme: data.tableTheme || 'classic',
    dealerImageUrl: data.dealerImageUrl || undefined,
  };
}

/**
 * Sort featured tables: PlayCoins first, then by tier
 */
function sortFeaturedTables(tables: FeaturedTable[]): FeaturedTable[] {
  const tierOrder: Record<string, number> = {
    'micro': 0,
    'low': 1,
    'mid': 2,
  };

  return tables.sort((a, b) => {
    // PlayCoins before PKCash
    if (a.currency !== b.currency) {
      return a.currency === 'playcoins' ? -1 : 1;
    }
    // Sort by tier within currency
    return (tierOrder[a.tier] || 0) - (tierOrder[b.tier] || 0);
  });
}

/**
 * Get the best featured table for quick match
 * Priority: Has players > PlayCoins Low > PKCash Low > Any
 */
export async function getBestFeaturedTable(
  preferredCurrency?: 'playcoins' | 'pkcash'
): Promise<FeaturedTable | null> {
  const tables = await getFeaturedTables();
  
  if (tables.length === 0) return null;

  // Filter by preferred currency if specified
  const candidates = preferredCurrency
    ? tables.filter(t => t.currency === preferredCurrency)
    : tables;

  if (candidates.length === 0) return tables[0];

  // Prefer tables with players but not full
  const tablesWithPlayers = candidates.filter(
    t => t.playerCount > 0 && t.playerCount < t.maxSeats
  );

  if (tablesWithPlayers.length > 0) {
    // Return the one with most players
    return tablesWithPlayers.reduce((a, b) => 
      a.playerCount > b.playerCount ? a : b
    );
  }

  // Return first available (lowest stakes)
  return candidates[0];
}

/**
 * Check if a featured table has open seats
 */
export function hasOpenSeats(table: FeaturedTable): boolean {
  return table.playerCount < table.maxSeats;
}

/**
 * Get wait time estimate for a full table
 * Returns estimated minutes until a seat opens
 */
export function getWaitTimeEstimate(table: FeaturedTable): number {
  if (hasOpenSeats(table)) return 0;
  // Rough estimate: 3-5 minutes per hand, players leave after ~20 hands
  return 15; // 15 minute estimate
}

// ============================================================================
// SERVICE EXPORT
// ============================================================================

const featuredTablesService = {
  // Config
  FEATURED_TABLES_CONFIG,
  
  // ID helpers
  getFeaturedTableId,
  isFeaturedTableId,
  getAllFeaturedTableIds,
  
  // Fetch
  getFeaturedTables,
  subscribeToFeaturedTables,
  getFeaturedTablesByCurrency,
  
  // Ensure tables exist
  ensureFeaturedTablesExist,
  
  // Quick match helpers
  getBestFeaturedTable,
  hasOpenSeats,
  getWaitTimeEstimate,
};

export default featuredTablesService;